package org.teddapp.project.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import com.tedd.db.User_profile
import androidx.compose.ui.graphics.asSkiaBitmap
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import org.jetbrains.skiko.wasm.onWasmReady
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.khronos.webgl.get
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.files.FileReader
import org.teddapp.project.db.DatabaseDriverFactory
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.db.WebDatabase
import org.teddapp.project.db.createRepositories
import org.teddapp.project.db.runTeddCatchUp
import org.teddapp.project.finance.FinanceScheduler
import org.teddapp.project.server.LocalServerSession
import org.teddapp.project.sync.rememberTeddSync
import org.teddapp.project.ui.AppSettings
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.TeddTheme
import org.teddapp.project.ui.admin.AdminScreen
import org.teddapp.project.ui.admin.PickedImage
import org.teddapp.project.ui.admin.TeddImagePicker
import org.teddapp.project.ui.backup.BackupScreen
import org.teddapp.project.ui.card.CardScreen
import org.teddapp.project.ui.finance.FinanceScreen
import org.teddapp.project.ui.focus.FocusScreen
import org.teddapp.project.ui.habits.HabitsScreen
import org.teddapp.project.ui.login.LoginScreen
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.notes.NotesScreen
import org.teddapp.project.ui.objectives.ObjectivesScreen
import org.teddapp.project.ui.portfolio.PortfolioScreen
import org.teddapp.project.ui.projects.ProjectsScreen
import org.teddapp.project.ui.settings.LocalSupabaseConnector
import org.teddapp.project.ui.settings.LocalSyncBindings
import org.teddapp.project.ui.skills.SkillsScreen
import org.teddapp.project.ui.today.TodayScreen
import org.teddapp.project.ui.vault.VaultScreen

/**
 * teddApp dans le navigateur. La base locale (SQLite en WebAssembly) et les réglages
 * se chargent d'abord ; l'interface, la même que partout ailleurs, s'affiche ensuite.
 */
@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    onWasmReady {
        MainScope().launch {
            WebDatabase.prepare()
            TeddImagePicker.pick = ::pickImage
            // Le navigateur ne garde pas les images en base : elle est réécrite en entier à chaque enregistrement.
            org.teddapp.project.sync.TeddFiles.keepDownloads = false
            installImageDownload()
            installTextFiles()
            org.teddapp.project.ui.mindmap.MindMapWheel.pixelsPerUnit = 1f
            val repos = createRepositories(DatabaseDriverFactory())
            val prefs = repos.preferences.loadAll()
            repos.userProfile.ensureExists()
            val profile = repos.userProfile.get()
            // Pas de jeu de démonstration ici : un navigateur est un appareil d'appoint, qui reçoit
            // ses données du serveur. Semé, chaque nouveau navigateur les renverrait au compte.
            FinanceScheduler.runDue(repos)
            runTeddCatchUp(repos.db)
            document.getElementById("chargement")?.remove()
            // Comme le site signale une page vue, l'application signale son ouverture : le
            // back-office compte ses visiteurs. Sans adresse d'API (essai local), rien n'est envoyé.
            defaultServerUrl().takeIf { it.isNotBlank() }?.let { api ->
                runCatching {
                    window.fetch(
                        "$api/public/app-visit",
                        org.w3c.fetch.RequestInit(method = "POST", body = """{"referrer":"${document.referrer.replace("\"", "")}"}"""),
                    )
                }
            }
            ComposeViewport(document.body!!) { WebApp(repos, prefs, profile) }
        }
    }
}

/**
 * Navigateur : une image (carte de visite, carte heuristique) se télécharge comme un
 * fichier PNG, par un lien de téléchargement créé à la volée.
 */
private fun installImageDownload() {
    org.teddapp.project.card.TeddShare.imageIsSaved = true
    org.teddapp.project.card.TeddShare.shareImage = { name, image ->
        val png = org.jetbrains.skia.Image.makeFromBitmap(image.asSkiaBitmap())
            .encodeToData(org.jetbrains.skia.EncodedImageFormat.PNG)
            ?: error("encodage PNG impossible")
        // Un ByteArray de Kotlin/JS est un Int8Array : le Blob le prend tel quel.
        val array = png.bytes.unsafeCast<org.khronos.webgl.Int8Array>()
        val blob = org.w3c.files.Blob(arrayOf(array), org.w3c.files.BlobPropertyBag(type = "image/png"))
        val url = org.w3c.dom.url.URL.createObjectURL(blob)
        val link = document.createElement("a").unsafeCast<org.w3c.dom.HTMLAnchorElement>()
        link.href = url
        link.download = name
        document.body?.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout({ org.w3c.dom.url.URL.revokeObjectURL(url) }, 10_000)
        "Image téléchargée : $name"
    }
}

/**
 * Navigateur : un Markdown exporté se télécharge (notes, portfolio), et une note
 * s'importe d'un fichier choisi dans la page.
 */
private fun installTextFiles() {
    org.teddapp.project.portfolio.export.PortfolioExporters.saveMarkdown = { name, text ->
        val blob = org.w3c.files.Blob(arrayOf(text), org.w3c.files.BlobPropertyBag(type = "text/markdown;charset=utf-8"))
        val url = org.w3c.dom.url.URL.createObjectURL(blob)
        val link = document.createElement("a").unsafeCast<org.w3c.dom.HTMLAnchorElement>()
        link.href = url
        link.download = name
        document.body?.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout({ org.w3c.dom.url.URL.revokeObjectURL(url) }, 10_000)
        name
    }
    org.teddapp.project.ui.notes.NoteImportPicker.pick = {
        suspendCoroutine { continuation ->
            val input = document.createElement("input").unsafeCast<HTMLInputElement>()
            input.type = "file"
            input.accept = ".md,.markdown,.txt,text/markdown,text/plain"
            var done = false
            fun finish(result: Pair<String, String>?) { if (!done) { done = true; continuation.resume(result) } }
            input.onchange = {
                val file = input.files?.item(0)
                if (file == null) finish(null) else {
                    val reader = FileReader()
                    reader.onload = { finish(file.name to reader.result.unsafeCast<String>()) }
                    reader.onerror = { finish(null) }
                    reader.readAsText(file)
                }
            }
            input.addEventListener("cancel", { finish(null) })
            input.click()
        }
    }
}

/** Navigateur : une photo se choisit par le sélecteur de fichiers de la page. */
private suspend fun pickImage(): PickedImage? = suspendCoroutine { continuation ->
    val input = document.createElement("input").unsafeCast<HTMLInputElement>()
    input.type = "file"
    input.accept = "image/jpeg,image/png,image/webp"
    var done = false
    fun finish(image: PickedImage?) { if (!done) { done = true; continuation.resume(image) } }
    input.onchange = {
        val file = input.files?.item(0)
        if (file == null) finish(null) else {
            val reader = FileReader()
            reader.onload = {
                val source = Int8Array(reader.result.unsafeCast<ArrayBuffer>())
                val bytes = ByteArray(source.length) { i -> source[i] }
                finish(TeddImagePicker.typeOf(bytes)?.let { PickedImage(bytes, it) })
            }
            reader.onerror = { finish(null) }
            reader.readAsArrayBuffer(file)
        }
    }
    // Annuler la boîte de dialogue ne déclenche « change » nulle part ; « cancel », si.
    input.addEventListener("cancel", { finish(null) })
    input.click()
}

/**
 * L'API du même domaine que la page : « app.teddfullstack.dev » propose
 * « https://api.teddfullstack.dev ». Ailleurs (essai local), rien n'est proposé.
 */
private fun defaultServerUrl(): String {
    val host = window.location.hostname
    return if (host.startsWith("app.")) "https://api." + host.removePrefix("app.") else ""
}

/** L'écran que désigne l'adresse du navigateur, s'il y en a un. */
private fun sectionInAddress(): Section? {
    val name = window.location.hash.removePrefix("#/").removePrefix("#")
    return Section.entries.firstOrNull { it.name.equals(name, ignoreCase = true) }
}

@Composable
private fun WebApp(repos: TeddRepositories, prefs: Map<String, String>, profile: User_profile?) {
    val appSettings = remember { AppSettings(repos.preferences, repos.userProfile, prefs, profile) }
    LaunchedEffect(Unit) {
        repos.userProfile.observe().collect { p -> if (p != null) appSettings.adoptProfile(p) }
    }
    val syncScope = remember { CoroutineScope(SupervisorJob() + Dispatchers.Default) }
    // Pas de noyau Rust dans le navigateur : la session s'appuie sur le serveur, qui
    // rejoue le journal. Les feuilles cochées ici deviennent des faits comme ailleurs.
    val coreSession = remember {
        org.teddapp.project.core.CoreSession(
            bridge = org.teddapp.project.core.openTeddCore("", "web"),
            scope = syncScope,
            deviceId = "web",
        ).also { core ->
            org.teddapp.project.db.TaskHooks.leafChanged = { skillId, taskId, done ->
                core.record(
                    org.teddapp.project.core.TeddIdentity.USER_ID,
                    if (done) org.teddapp.project.core.CoreFacts.parcoursLeafCompleted(skillId, taskId)
                    else org.teddapp.project.core.CoreFacts.parcoursLeafReopened(skillId, taskId),
                )
            }
        }
    }
    val sync = rememberTeddSync(
        repos = repos,
        appSettings = appSettings,
        syncScope = syncScope,
        deviceName = "Navigateur",
        platform = "web",
        coreSession = coreSession,
        serverUrl = defaultServerUrl(),
    )
    CompositionLocalProvider(
        LocalServerSession provides sync.session,
        org.teddapp.project.core.LocalCoreSession provides coreSession,
        // Les modèles d'écran survivent aux rubriques : revenir ne relit rien.
        org.teddapp.project.ui.LocalScreenModels provides org.teddapp.project.ui.rememberScreenModels(),
        LocalAppSettings provides appSettings,
        LocalSyncBindings provides sync.bindings,
        LocalSupabaseConnector provides sync.supabaseConnector,
    ) {
        TeddTheme(themeMode = appSettings.themeMode) {
            var loggedIn by remember { mutableStateOf(false) }
            // Chaque écran a son adresse (#/focus, #/notes…) : les boutons Précédent et Suivant
            // du navigateur fonctionnent, et une adresse copiée rouvre le même écran.
            var section by remember { mutableStateOf(sectionInAddress() ?: Section.Today) }
            val go: (Section) -> Unit = { section = it }
            DisposableEffect(Unit) {
                val onChange: (Event) -> Unit = { sectionInAddress()?.let { section = it } }
                window.addEventListener("hashchange", onChange)
                onDispose { window.removeEventListener("hashchange", onChange) }
            }
            LaunchedEffect(section, loggedIn) {
                if (!loggedIn) return@LaunchedEffect
                val address = "#/" + section.name.lowercase()
                if (window.location.hash != address) window.location.hash = address
                document.title = "${section.label} · teddApp"
            }
            if (!loggedIn) {
                LoginScreen(accounts = repos.accounts, onLoginSuccess = { loggedIn = true })
            } else when (section) {
                Section.Today -> TodayScreen(repos = repos, onNavigate = go)
                Section.Habits -> HabitsScreen(repos = repos, onNavigate = go)
                Section.Projects -> ProjectsScreen(repos = repos, onNavigate = go)
                Section.Vault -> VaultScreen(repos = repos, onNavigate = go)
                Section.Card -> CardScreen(repos = repos, onNavigate = go)
                Section.MindMaps -> org.teddapp.project.ui.mindmap.MindMapScreen(repos = repos, onNavigate = go)
                Section.Admin -> AdminScreen(onNavigate = go, repos = repos)
                Section.Skills -> SkillsScreen(repos = repos, onNavigate = go)
                Section.Focus -> FocusScreen(repos = repos, onNavigate = go)
                Section.Notes -> NotesScreen(repos = repos, onNavigate = go)
                Section.Finance -> FinanceScreen(repos = repos, onNavigate = go)
                Section.Portfolio -> PortfolioScreen(repos = repos, onNavigate = go)
                // Les exports en fichier ne sont pas encore branchés dans le navigateur.
                Section.Backup -> BackupScreen(onNavigate = go, onExportJson = { null }, onExportSql = { null }, db = repos.db)
                else -> ObjectivesScreen(repos = repos, onNavigate = go)
            }
        }
    }
}
